// The Coordinator Workbench's pure helpers (lane B5 S11): the URL's filter, the words a stored status, meeting type,
// conflict or history kind is drawn with, the DC-35 action a row offers, the SLA tier, and whether this seat acts on a
// tool ordinarily (its declared roles) or only through the audited override (a view seat on this screen).

import type { Manifest, Me, SchedulingWorkbenchInput, WorkbenchRowOut } from "@/api/tools";
import {
  ACKNOWLEDGMENT_STATUS, ACTION_LABELS, CORE_LABELS, COMMON_LABELS, FINAL_LOOP_SCHEDULING_STATUS, LOOP_LABELS, RESPONSE_STATUS,
  ROLE_LABELS, SCHEDULING_LABELS, SCHEDULING_STATUS, SYNC_LABELS, type DueTier,
} from "@/mc";
import { SCHEDULING_ROW_TEMPLATES } from "../actions/labels";
import { MEETING_CARD_LABELS, MEETING_TEMPLATE_LABELS, SCHEDULING_WORDS, WORKBENCH_CHIP_LABELS } from "./labels";

/**
 * A record id as one segment of a hotspot key (CAPTURE.md H1: `[a-z0-9-]` only): `sr_1a2b3c4d` → `sr-1a2b3c4d`,
 * `R-1040` → `r-1040`. Without it an id-keyed control carries a key the capture harness refuses, failing every frame
 * of the screen.
 */
export function hsId(id: string): string {
  return id.toLowerCase().replace(/[^a-z0-9-]+/g, "-");
}

export type StatusKey = NonNullable<SchedulingWorkbenchInput["status"]>[number];
export type MeetingType = NonNullable<SchedulingWorkbenchInput["meeting_type"]>[number];

/** The eight queue groups in workflow order (BRD §4.2), each with his word. */
export const STATUS_ORDER: readonly StatusKey[] = [
  "requested", "in_progress", "awaiting_candidate", "awaiting_interviewer", "scheduled", "reschedule_requested", "completed", "cancelled",
];

export const STATUS_WORD: Readonly<Record<StatusKey, string>> = {
  requested: SCHEDULING_STATUS.requested,
  in_progress: SCHEDULING_STATUS.schedulingInProgress,
  awaiting_candidate: SCHEDULING_STATUS.awaitingCandidateAvailability,
  awaiting_interviewer: SCHEDULING_STATUS.awaitingInterviewerAvailability,
  scheduled: SCHEDULING_STATUS.scheduled,
  reschedule_requested: SCHEDULING_STATUS.rescheduleRequested,
  completed: SCHEDULING_STATUS.completed,
  cancelled: SCHEDULING_STATUS.cancelled,
};

/** Completed and Cancelled start collapsed: "completed items close but remain historical." */
export const HISTORY_STATUSES: ReadonlySet<StatusKey> = new Set(["completed", "cancelled"]);

export const MEETING_TYPES: readonly MeetingType[] = ["intake", "recruiter_screen", "hm_interview", "final_loop", "debrief", "offer_call"];

/** A row's title: the meeting template's name. */
export const TEMPLATE_WORD: Readonly<Record<MeetingType, string>> = {
  intake: MEETING_TEMPLATE_LABELS.intake,
  recruiter_screen: MEETING_TEMPLATE_LABELS.recruiterScreen,
  hm_interview: MEETING_TEMPLATE_LABELS.hmInterview,
  final_loop: MEETING_TEMPLATE_LABELS.finalInterviewLoop,
  debrief: MEETING_TEMPLATE_LABELS.debrief,
  offer_call: MEETING_TEMPLATE_LABELS.offerCall,
};

/** The kind chips: "Intakes, recruiter screens, HM interviews, Final Interview Loops and debriefs to schedule." */
export const KIND_CHIP: Readonly<Record<MeetingType, string>> = {
  intake: WORKBENCH_CHIP_LABELS.intakes,
  recruiter_screen: WORKBENCH_CHIP_LABELS.recruiterScreens,
  hm_interview: WORKBENCH_CHIP_LABELS.hmInterviews,
  final_loop: WORKBENCH_CHIP_LABELS.finalInterviewLoops,
  debrief: WORKBENCH_CHIP_LABELS.debriefs,
  offer_call: WORKBENCH_CHIP_LABELS.offerCall,
};

const isStatus = (value: string): value is StatusKey => (STATUS_ORDER as readonly string[]).includes(value);
const isType = (value: string): value is MeetingType => (MEETING_TYPES as readonly string[]).includes(value);

export const templateWord = (type: string): string => (isType(type) ? TEMPLATE_WORD[type] : type);

/** The queue row's What column (DC-241, B13 F-162): a request whose reschedule was asked reads **Reschedule request ·
 *  {meeting} · {requisition id}** as its name — the Action Center row's words — with the candidate alone beneath it;
 *  every other row names its meeting template, the candidate and the requisition beneath. */
export function queueRowWords(
  row: Pick<WorkbenchRowOut, "status" | "meeting_type" | "requisition_id" | "candidate_display_name">,
): { name: string; sub: string } {
  if (row.status === "reschedule_requested") {
    return {
      name: SCHEDULING_ROW_TEMPLATES.rescheduleRequest({ meeting: cardWord(row.meeting_type), "requisition id": row.requisition_id }),
      sub: row.candidate_display_name ?? "",
    };
  }
  return { name: templateWord(row.meeting_type), sub: [row.candidate_display_name, row.requisition_id].filter(Boolean).join(" · ") };
}

/** The meeting's name on a card (G §9 "Meeting names on cards"), as the server titles its Action Center rows: the
 *  intake's is *Intake call*; every other type's card name is its template word. */
export const cardWord = (type: string): string => (type === "intake" ? MEETING_CARD_LABELS.intakeCall : templateWord(type));
export const statusWord = (status: string): string => (isStatus(status) ? STATUS_WORD[status] : status);

/** The Workbench's filter as the URL carries it (`#workbench[/<sr>]?status=&type=&risk=sla&blocked=1&sync=1&req=`). */
export interface WorkbenchQuery {
  status: StatusKey | null;
  type: MeetingType | null;
  risk: boolean;
  blocked: boolean;
  sync: boolean;
  req: string | null;
}

export function parseQuery(raw: string): WorkbenchQuery {
  const query = new URLSearchParams(raw.split("?")[1] ?? "");
  const status = query.get("status") ?? "";
  const type = query.get("type") ?? "";
  const req = query.get("req") ?? "";
  return {
    status: isStatus(status) ? status : null,
    type: isType(type) ? type : null,
    risk: query.get("risk") === "sla",
    blocked: query.get("blocked") === "1",
    sync: query.get("sync") === "1",
    req: /^R-\d+$/.test(req) ? req : null,
  };
}

export function queryString(q: WorkbenchQuery): string {
  const query = new URLSearchParams();
  if (q.status) query.set("status", q.status);
  if (q.type) query.set("type", q.type);
  if (q.risk) query.set("risk", "sla");
  if (q.blocked) query.set("blocked", "1");
  if (q.sync) query.set("sync", "1");
  if (q.req) query.set("req", q.req);
  const text = query.toString();
  return text ? `?${text}` : "";
}

/** The one `scheduling.workbench` read the page lists: every group (open and history) under the URL's filter. */
export function workbenchInput(q: WorkbenchQuery): SchedulingWorkbenchInput {
  return {
    view: "all",
    status: q.status ? [q.status] : null,
    meeting_type: q.type ? [q.type] : null,
    risk: q.risk ? ["at_risk", "breached"] : null,
    blocked: q.blocked ? true : null,
    sync_failed: q.sync ? true : null,
    requisition_id: q.req,
    limit: 200,
  };
}

/** The SLA tier the row's risk reaches (DC-19: info *Due* → flag *Near-breach* → red *Overdue*). */
export function dueTier(risk: string): DueTier {
  return risk === "breached" ? "overdue" : risk === "at_risk" ? "nearBreach" : "due";
}

/** The blocked rule, his sentence ("Cannot schedule if required panel assignment is declined or coverage invalid."). */
export const blockedRule = (row: { blocked_reason?: string | null }): string | null =>
  row.blocked_reason ? SCHEDULING_LABELS.panelDeclined : null;

export type RowAction =
  | { kind: "page"; label: string; href: string }
  | { kind: "remind"; label: string }
  | { kind: "reschedule"; label: string; href: string };

/**
 * The page a request is proposed and scheduled on (S12): the intake call, the round pages, the Final Interview Loop page
 * and the debrief; the offer call (no page of its own yet) opens its request beside the queue.
 */
export function pageOf(row: Pick<WorkbenchRowOut, "meeting_type" | "requisition_id" | "application_id" | "cycle_id" | "scheduling_request_id">): string {
  const r = row.requisition_id;
  const aid = row.application_id;
  switch (row.meeting_type) {
    case "intake":
      return `#requisitions/${r}/intake/call`;
    case "recruiter_screen":
      return aid ? `#requisitions/${r}/candidates/${aid}/rounds/1` : `#requisitions/${r}`;
    case "hm_interview":
      return aid ? `#requisitions/${r}/candidates/${aid}/rounds/2` : `#requisitions/${r}`;
    case "final_loop":
      return aid ? `#requisitions/${r}/candidates/${aid}/schedule` : `#requisitions/${r}`;
    case "debrief":
      return row.cycle_id ? `#requisitions/${r}/debriefs/${row.cycle_id}` : `#requisitions/${r}`;
    default:
      return `#workbench/${row.scheduling_request_id ?? ""}`;
  }
}

/** DC-35: Requested → Propose slots · Scheduling in Progress → Schedule · both Awaiting → Remind · Reschedule Requested →
 *  Reschedule · Scheduled / Completed / Cancelled → no button. */
export function rowAction(row: WorkbenchRowOut): RowAction | null {
  if (!row.scheduling_request_id) return null;
  switch (row.status) {
    case "requested":
      return { kind: "page", label: ACTION_LABELS.proposeSlots, href: pageOf(row) };
    case "in_progress":
      return { kind: "page", label: ACTION_LABELS.schedule, href: pageOf(row) };
    case "awaiting_candidate":
    case "awaiting_interviewer":
      return { kind: "remind", label: SCHEDULING_WORDS.remind };
    case "reschedule_requested":
      return { kind: "reschedule", label: ACTION_LABELS.reschedule, href: `#workbench/${row.scheduling_request_id}?open=reschedule` };
    default:
      return null;
  }
}

/** The role words an attendee or a conflict's subject is drawn with. */
export const ROLE_WORD: Readonly<Record<string, string>> = {
  hm: ROLE_LABELS.hm,
  recruiter: ROLE_LABELS.recruiter,
  hrbp: ROLE_LABELS.hrbp,
  interviewer: ROLE_LABELS.interviewer,
  coordinator: ROLE_LABELS.coordinator,
  room: LOOP_LABELS.room,
};

/** A conflict's reason in his words (G §9 "Check availability, working hours, OOF, time zone, rooms and conflicts"). */
export function conflictWord(kind: string): string {
  switch (kind) {
    case "oof": return SCHEDULING_LABELS.oof;
    case "outside_hours": return SCHEDULING_LABELS.workingHours;
    case "tz_invalid": return SCHEDULING_LABELS.timeZone;
    case "room": return SCHEDULING_LABELS.room;
    case "sequence": return SCHEDULING_WORDS.invalidSequence;
    case "optional_attendee": return COMMON_LABELS.optional;
    default: return SCHEDULING_LABELS.busy;
  }
}

/** The format word of a meeting ("Coordinator schedules virtual/onsite/hybrid rounds …"). */
export function formatWord(format: string | null | undefined): string | null {
  return format === "virtual" ? SCHEDULING_LABELS.virtual : format === "onsite" ? SCHEDULING_LABELS.onsite
    : format === "hybrid" ? SCHEDULING_LABELS.hybrid : null;
}

/** The response an attendee row shows (G §10: Accepted · Declined · Acknowledgment Pending). */
export function responseStatus(response: string): "accepted" | "declined" | "pending" {
  return response === "accepted" ? "accepted" : response === "declined" ? "declined" : "pending";
}

/** A history row's sentence: the state the request reached, in his words. */
export function historyWord(event: { kind: string; to_status?: string | null; detail: Record<string, unknown> }): string {
  switch (event.kind) {
    case "requested": return SCHEDULING_STATUS.requested;
    case "status": return statusWord(event.to_status ?? "");
    case "blocked": return FINAL_LOOP_SCHEDULING_STATUS.blocked;
    case "unblocked": return FINAL_LOOP_SCHEDULING_STATUS.readyToSchedule;
    case "conflict_detected":
    case "conflict_override": return SCHEDULING_LABELS.conflict;
    case "no_response": return RESPONSE_STATUS.nonresponse;
    case "response": {
      const answer = String(event.detail["response"] ?? "");
      return answer === "accepted" ? ACKNOWLEDGMENT_STATUS.accepted : answer === "declined" ? ACKNOWLEDGMENT_STATUS.declined
        : ACKNOWLEDGMENT_STATUS.acknowledgmentPending;
    }
    case "reschedule_requested": return SCHEDULING_STATUS.rescheduleRequested;
    case "reschedule_declined": return ACKNOWLEDGMENT_STATUS.declined;
    case "sync_failed": return CORE_LABELS.syncFailure;
    case "sync_recovered": return SYNC_LABELS.delivered;
    case "completed": return SCHEDULING_STATUS.completed;
    case "cancelled": return SCHEDULING_STATUS.cancelled;
    default: return statusWord(event.to_status ?? event.kind);
  }
}

/**
 * Whether this seat calls `tool` ordinarily — through a role the tool declares — rather than only through the audited
 * override (BRD ch. 13 "View/override"): the manifest decides, never a role table here. An override-only seat reads the
 * Workbench as a view; its overrides are the request page's (a reason is asked there).
 */
export function acts(manifest: Manifest | null, me: Me | null, tool: string): boolean {
  const entry = manifest?.tools.find((t) => t.name === tool);
  if (!entry || !me) return false;
  const declared = entry._meta["com.gotofoods.ta/roles"];
  const override = entry._meta["com.gotofoods.ta/override"];
  const roles = Array.isArray(declared) ? (declared as string[]) : [];
  const viaOverride = Array.isArray(override) ? (override as string[]) : [];
  return me.roles.some((role) => roles.includes(role) && !viaOverride.includes(role));
}
